import type { CSSProperties, ReactNode } from 'react';

// One pattern for every section of a server's Settings tab: a heading with its action on the right, one line saying
// the current state, then what the section holds. A framed panel only around a data visualisation.

const TONE = { normal: 'var(--color-text-2)', muted: 'var(--color-text-3)', warn: 'var(--color-warn)', error: 'var(--color-error)' } as const;

export function SettingsSection({ id, title, state, tone = 'normal', loading = false, action, children }: {
  /** id of the heading, also the anchor links land on */
  id: string;
  title: string;
  /** The state line; while `loading`, a skeleton line of the same height takes its place */
  state?: ReactNode;
  tone?: keyof typeof TONE;
  loading?: boolean;
  action?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <section aria-labelledby={id} aria-busy={loading || undefined} className="settings-section min-w-0 scroll-mt-4 py-6 first:pt-0">
      <div className="flex items-baseline justify-between gap-4">
        <h2 id={id} className="text-lg font-semibold" style={{ color: 'var(--color-text)' }}>{title}</h2>
        {action}
      </div>
      {loading ? <SkeletonLine /> : state && <p className="mt-0.5 max-w-[68ch] text-sm" style={{ color: TONE[tone] }}>{state}</p>}
      {children}
    </section>
  );
}

/** The action of a section, on the right of its heading: a text button, a full tap target on phones */
export function SectionAction({ children, onClick, disabled }: { children: ReactNode; onClick: () => void; disabled?: boolean }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled}
      className="flex-shrink-0 rounded-sm text-xs hover:underline focus-visible:outline focus-visible:outline-2 disabled:opacity-60 max-sm:min-h-10 max-sm:px-1"
      style={{ color: 'var(--color-brand-light)' }}>
      {children}
    </button>
  );
}

/** A line of text not loaded yet: keeps its place so nothing shifts when it arrives */
export function SkeletonLine({ width = '70%' }: { width?: string }) {
  return (
    <span role="presentation" className="mt-1.5 block h-3.5 max-w-full animate-pulse rounded" data-testid="skeleton-line"
      style={{ width, backgroundColor: 'var(--color-surface-3)' }} />
  );
}

/** The frame of a data visualisation inside a section */
export function SettingsPanel({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return <div className="settings-panel mt-4 px-4 pb-4 pt-3" style={style}>{children}</div>;
}
